import { gql, useLazyQuery } from "@apollo/client";
import { Autocomplete, Card, CircularProgress, Fade, Icon, Modal, TextField, Tooltip } from "@mui/material";
import SoftBox from "components/CheckBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

export default function ModalCambioProducto({ data, handleClose, setSelection, defaultOptions }) {
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const [getMateriasPrimas] = useLazyQuery(
    gql`
      query getMateriasPrimasModalCambioProducto($filter: JSON, $search: String) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(filter: $filter, search: $search, order: { field: "nombre", order: ASC }) {
          id
          codigo
          tipo
          nombre
          categoria
          idMateriaPrimaReferencia
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );

  const fetchMateriasPrimas = (search = "") => {
    setLoading(true);
    return getMateriasPrimas({
      variables: {
        filter: {
          disabled: false,
        },
        search,
      },
    })
      .then((result) => {
        setOptions(result?.data?.materiasPrimas || []);
      })
      .finally(() => setLoading(false));
  };

  const getInitialSearch = () => {
    if (!data?.nombre) return "";
    return String(data.nombre).trim().split(" ")[0] || "";
  };

  const handleSearch = (value) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const normalizedValue = (value || "").trim();
    const timeout = setTimeout(() => {
      fetchMateriasPrimas(normalizedValue);
    }, 500);

    setDebounceTimeout(timeout);
  };

  useEffect(() => {
    if (Boolean(data)) {
      const optionsByDefault = Array.isArray(defaultOptions) ? defaultOptions : [];
      setSelected(data?.materiaPrimaDestino || null);
      setInputValue(
        data?.materiaPrimaDestino
          ? `${data.materiaPrimaDestino.id} - ${data.materiaPrimaDestino.nombre}`
          : ""
      );
      if (optionsByDefault.length > 0) {
        setOptions(optionsByDefault);
      } else {
        fetchMateriasPrimas(getInitialSearch());
      }
    } else {
      setOptions([]);
      setInputValue("");
      setSelected(null);
    }
  }, [data, defaultOptions]);

  useEffect(() => {
    if (!Boolean(data)) return;
    const normalizedInput = (inputValue || "").trim();
    if (!normalizedInput) {
      const optionsByDefault = Array.isArray(defaultOptions) ? defaultOptions : [];
      if (optionsByDefault.length > 0) {
        setOptions(optionsByDefault);
        return;
      }
    }

    const selectedName = `${selected?.id} - ${selected?.nombre}`;
    if (normalizedInput && inputValue !== selectedName) {
      handleSearch(normalizedInput);
    }
  }, [inputValue, data, selected, defaultOptions]);

  useEffect(() => {
    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [debounceTimeout]);

  if (!data) return null;

  return (
    <Modal open={Boolean(data)} onClose={handleClose} closeAfterTransition>
      <Fade in={Boolean(data)}>
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: "90%",
            width: { xs: "80%", sm: "60%", xxl: "30%" },
            overflow: "auto",
            p: 3,
          }}
        >
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6">Cambiar materia prima</SoftTypography>
            <Tooltip title="Cerrar" placement="top">
              <SoftButton circular iconOnly color="error" variant="gradient" onClick={handleClose}>
                <Icon>close</Icon>
              </SoftButton>
            </Tooltip>
          </SoftBox>

          <SoftBox>
            <SoftTypography variant="button">Materia prima actual</SoftTypography>
            <SoftInput
              value={`${data.id} - ${data.nombre} - (Ref: ${
                data.idMateriaPrimaReferencia ? data.idMateriaPrimaReferencia : data.id
              })`}
              readOnly
            />
          </SoftBox>

          <SoftBox display="flex" justifyContent="center" alignItems="center" mt={3}>
            <Icon color="primary" fontSize="large">
              swap_vertical
            </Icon>
          </SoftBox>

          <SoftBox>
            <SoftTypography variant="button">Nueva materia prima</SoftTypography>
            <Autocomplete
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.id} - ${option.nombre}`}
              options={options}
              loading={loading}
              noOptionsText="Sin resultados"
              loadingText="Cargando..."
              filterOptions={(x) => x}
              value={selected}
              onChange={(event, newValue) => setSelected(newValue)}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              ListboxProps={{ style: { maxHeight: 200 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar materia prima"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                    startAdornment: (
                      <>
                        <Icon fontSize="small" sx={{ mr: 1 }}>
                          search
                        </Icon>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </SoftBox>

          <SoftBox display="flex" justifyContent="end" alignItems="center" mt={2}>
            <SoftButton
              variant="gradient"
              color="primary"
              circular
              disabled={!selected}
              onClick={() => {
                setSelection({ selected, data });
                handleClose();
              }}
            >
              <Icon>done</Icon>
              Cambiar
            </SoftButton>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCambioProducto.defaultProps = {
  handleClose: () => {},
  setSelection: () => {},
  defaultOptions: [],
};

ModalCambioProducto.propTypes = {
  data: PropTypes.object,
  handleClose: PropTypes.func,
  setSelection: PropTypes.func,
  defaultOptions: PropTypes.array,
};
